import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import * as userService from '../services/userService';
import { 
    UserIcon, 
    PencilSquareIcon, 
    TrashIcon, 
    CheckCircleIcon, 
    XCircleIcon,
    KeyIcon,
    ShieldCheckIcon,
    SparklesIcon,
    IdentificationIcon,
    DocumentTextIcon,
    PaperClipIcon,
    ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

// Definición estática de módulos para evitar recrearla en cada render
const SYSTEM_MODULES = [
    { id: 'reports', label: 'Informes de Visita' },
    { id: 'techReports', label: 'Informes Técnicos' },
    { id: 'quotes', label: 'Cotizaciones', canApprove: true }, 
    { id: 'products', label: 'Inventario' },
    { id: 'providers', label: 'Proveedores y Compras' }, 
    { id: 'finance', label: 'Finanzas y Gastos' },        
    { id: 'accounting', label: 'Contabilidad' },
    { id: 'clients', label: 'Clientes' },
    { id: 'hr', label: 'Personal (RRHH)' },
    { id: 'mail', label: 'Correos' },
    { id: 'calendar', label: 'Agenda y Planificación' }, 
    { id: 'tools', label: 'Herramientas Técnicas', canView: true }, 
    { id: 'parameters', label: 'Configuración' }
];

// --- SUB-COMPONENTE: MODAL DE EDICIÓN ---
const UserModal = ({ userToEdit, onClose, onSave, setNotification, currentUser }) => {
    // UI State
    const [activeTab, setActiveTab] = useState('access');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadingDoc, setUploadingDoc] = useState(false);
    const fileInputRef = useRef(null);

    // Form State
    const [formData, setFormData] = useState({
        email: userToEdit?.email || '',
        password: '',
        role: userToEdit?.role || 'user',
        permissions: userToEdit?.permissions || {},
        fullName: userToEdit?.fullName || '',
        rut: userToEdit?.rut || '',
        phone: userToEdit?.phone || '',
        address: userToEdit?.address || '',
        jobTitle: userToEdit?.jobTitle || '',
        emergencyContactName: userToEdit?.emergencyContactName || '',
        emergencyContactPhone: userToEdit?.emergencyContactPhone || '',
        documents: userToEdit?.documents || []
    });

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handlePermissionChange = (moduleId, type) => {
        setFormData(prev => ({
            ...prev,
            permissions: {
                ...prev.permissions,
                [moduleId]: {
                    ...prev.permissions[moduleId],
                    [type]: !prev.permissions[moduleId]?.[type]
                }
            }
        }));
    };

    // --- MANEJO DE DOCUMENTOS (Delegado al servicio) ---
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        if (!userToEdit) {
            setNotification({ message: 'Guarda el usuario antes de subir documentos.', type: 'warning' });
            return;
        }

        setUploadingDoc(true);
        try {
            const updatedDocs = await userService.uploadUserDocument(userToEdit.id, file, formData.documents);
            handleChange('documents', updatedDocs);
            setNotification({ message: 'Documento subido.', type: 'success' });
        } catch (error) {
            setNotification({ message: 'Error al subir documento.', type: 'error' });
        } finally {
            setUploadingDoc(false);
            if(fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDeleteDocument = async (docIndex) => {
        if (!window.confirm('¿Eliminar documento?')) return;
        
        const docToDelete = formData.documents[docIndex];
        const newDocs = formData.documents.filter((_, i) => i !== docIndex);

        try {
            // Pasamos docToDelete para que el servicio intente borrarlo de Storage también
            await userService.deleteUserDocumentReference(userToEdit.id, newDocs, docToDelete);
            handleChange('documents', newDocs);
            setNotification({ message: 'Documento eliminado.', type: 'success' });
        } catch (error) {
            setNotification({ message: 'Error al eliminar.', type: 'error' });
        }
    };

    // --- GUARDADO ---
    const handleSave = async (e) => {
        e.preventDefault();
        
        // Validaciones de Permisos
        const userPermissions = currentUser.permissions || {};
        const isAdmin = currentUser.role === 'admin' || currentUser.role === 'superuser';
        const isSuperUser = currentUser.role === 'superuser';

        if (!userToEdit && !isAdmin && !userPermissions.hr?.create) {
            setNotification({ message: 'No tienes permiso para crear usuarios.', type: 'error' });
            return;
        }
        if (formData.role === 'superuser' && !isSuperUser) {
             setNotification({ message: 'Solo un Super Usuario puede crear otros.', type: 'error' });
             return;
        }

        setIsSubmitting(true);
        try {
            // Separamos password y email del resto de la data para Firestore
            const { password, email, ...profileData } = formData;

            if (userToEdit) {
                await userService.updateUser(userToEdit.id, profileData);
                setNotification({ message: 'Usuario actualizado.', type: 'success' });
            } else {
                // Crear usuario (Lógica compleja en servicio)
                await userService.createUser(email, password, profileData);
                setNotification({ message: 'Usuario creado exitosamente.', type: 'success' });
            }
            onSave(); 
            onClose();
        } catch (error) {
            let msg = 'Error al guardar.';
            if (error.code === 'auth/email-already-in-use') msg = 'El correo ya existe.';
            if (error.code === 'auth/weak-password') msg = 'Contraseña muy débil.';
            setNotification({ message: msg, type: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const isCurrentUserSuper = currentUser.role === 'superuser';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm animate-fade-in transition-colors">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col transition-colors">
                
                {/* HEADER */}
                <div className="p-6 bg-blue-900 dark:bg-blue-950 text-white flex justify-between items-center transition-colors">
                    <h2 className="text-xl font-bold flex items-center gap-2">
                        <UserIcon className="w-6 h-6"/>
                        {userToEdit ? `Editar: ${userToEdit.email}` : 'Crear Nuevo Colaborador'}
                    </h2>
                    <button onClick={onClose} className="text-blue-200 hover:text-white transition-colors"><XCircleIcon className="w-8 h-8"/></button>
                </div>

                {/* TABS */}
                <div className="flex border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900 transition-colors">
                    <button onClick={() => setActiveTab('access')} className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${activeTab === 'access' ? 'border-b-4 border-blue-600 text-blue-800 dark:text-blue-400 bg-white dark:bg-gray-800' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
                        <KeyIcon className="w-5 h-5"/> Acceso
                    </button>
                    <button onClick={() => setActiveTab('personal')} className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${activeTab === 'personal' ? 'border-b-4 border-blue-600 text-blue-800 dark:text-blue-400 bg-white dark:bg-gray-800' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
                        <IdentificationIcon className="w-5 h-5"/> Ficha
                    </button>
                    <button onClick={() => setActiveTab('docs')} className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${activeTab === 'docs' ? 'border-b-4 border-blue-600 text-blue-800 dark:text-blue-400 bg-white dark:bg-gray-800' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
                        <DocumentTextIcon className="w-5 h-5"/> Docs ({formData.documents.length})
                    </button>
                </div>

                {/* CONTENT */}
                <div className="p-6 overflow-y-auto flex-1 bg-white dark:bg-gray-800 transition-colors">
                    <form id="userForm" onSubmit={handleSave} className="space-y-6">
                        
                        {/* TAB 1: ACCESO */}
                        {activeTab === 'access' && (
                            <div className="space-y-6 animate-fadeIn transition-colors">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-blue-50 dark:bg-blue-900/10 p-4 rounded-lg border border-blue-100 dark:border-blue-900/30 transition-colors">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                                        <input type="email" value={formData.email} onChange={e => handleChange('email', e.target.value)} className="w-full border dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-800 transition-colors" disabled={!!userToEdit} required />
                                    </div>
                                    {!userToEdit && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contraseña</label>
                                            <input type="password" value={formData.password} onChange={e => handleChange('password', e.target.value)} className="w-full border dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors" required placeholder="Min. 6 caracteres" />
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rol</label>
                                        <select value={formData.role} onChange={e => handleChange('role', e.target.value)} className="w-full border dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-800 transition-colors" disabled={currentUser.role !== 'admin' && currentUser.role !== 'superuser'}>
                                            <option value="user">Estándar</option>
                                            {(currentUser.role === 'admin' || isCurrentUserSuper) && <option value="admin">Administrador</option>}
                                            {isCurrentUserSuper && <option value="superuser">Super Usuario</option>}
                                        </select>
                                    </div>
                                </div>

                                {formData.role !== 'admin' && formData.role !== 'superuser' && (
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center transition-colors"><ShieldCheckIcon className="w-5 h-5 mr-2 text-green-600 dark:text-green-500"/> Permisos Granulares</h3>
                                        <div className="overflow-x-auto border dark:border-gray-700 rounded-lg transition-colors">
                                            <table className="min-w-full text-sm text-left">
                                                <thead className="bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-gray-300 uppercase text-xs transition-colors">
                                                    <tr>
                                                        <th className="px-4 py-3">Módulo</th>
                                                        {['view', 'create', 'edit', 'delete', 'approve'].map(h => <th key={h} className="px-4 py-3 text-center">{h === 'approve' ? 'Aprobar' : h}</th>)}
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 transition-colors">
                                                    {SYSTEM_MODULES.map(mod => (
                                                        <tr key={mod.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-gray-800 dark:text-gray-200">
                                                            <td className="px-4 py-2 font-medium">{mod.label}</td>
                                                            {['view', 'create', 'edit', 'delete'].map(type => (
                                                                <td key={type} className="px-4 py-2 text-center">
                                                                    {(!mod.canView || type === 'view' || mod.canView === true) ? ( // Simple logic fix for visualization
                                                                        <input type="checkbox" 
                                                                            checked={formData.permissions[mod.id]?.[type] || false} 
                                                                            onChange={() => handlePermissionChange(mod.id, type)} 
                                                                            className="rounded text-blue-600 dark:text-blue-500 w-4 h-4 cursor-pointer focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                                                                        />
                                                                    ) : <span className="text-gray-300 dark:text-gray-600">-</span>}
                                                                </td>
                                                            ))}
                                                            <td className="px-4 py-2 text-center">
                                                                {mod.canApprove ? (
                                                                    <input type="checkbox" 
                                                                        checked={formData.permissions[mod.id]?.approve || false} 
                                                                        onChange={() => handlePermissionChange(mod.id, 'approve')} 
                                                                        className="rounded text-green-600 dark:text-green-500 w-4 h-4 cursor-pointer focus:ring-green-500 dark:bg-gray-700 dark:border-gray-600"
                                                                    />
                                                                ) : <span className="text-gray-300 dark:text-gray-600">-</span>}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* TAB 2: FICHA PERSONAL */}
                        {activeTab === 'personal' && (
                            <div className="space-y-6 animate-fadeIn transition-colors">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Nombre Completo</label>
                                        <input type="text" value={formData.fullName} onChange={e => handleChange('fullName', e.target.value)} className="w-full border dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg p-2 transition-colors" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">RUT</label>
                                        <input type="text" value={formData.rut} onChange={e => handleChange('rut', e.target.value)} className="w-full border dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg p-2 transition-colors" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Cargo</label>
                                        <input type="text" value={formData.jobTitle} onChange={e => handleChange('jobTitle', e.target.value)} className="w-full border dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg p-2 transition-colors" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Teléfono</label>
                                        <input type="text" value={formData.phone} onChange={e => handleChange('phone', e.target.value)} className="w-full border dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg p-2 transition-colors" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Dirección</label>
                                        <input type="text" value={formData.address} onChange={e => handleChange('address', e.target.value)} className="w-full border dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg p-2 transition-colors" />
                                    </div>
                                </div>
                                <h3 className="text-lg font-bold text-red-800 dark:text-red-400 border-b dark:border-gray-700 pb-2 mt-4 transition-colors">Emergencia</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Nombre Contacto</label>
                                        <input type="text" value={formData.emergencyContactName} onChange={e => handleChange('emergencyContactName', e.target.value)} className="w-full border dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg p-2 transition-colors" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Fono Contacto</label>
                                        <input type="text" value={formData.emergencyContactPhone} onChange={e => handleChange('emergencyContactPhone', e.target.value)} className="w-full border dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg p-2 transition-colors" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* TAB 3: DOCUMENTOS */}
                        {activeTab === 'docs' && (
                            <div className="space-y-6 animate-fadeIn transition-colors">
                                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg border border-dashed border-gray-400 dark:border-gray-600 text-center transition-colors">
                                    {!userToEdit ? (
                                        <p className="text-yellow-600 dark:text-yellow-500">Guarda el usuario primero para subir documentos.</p>
                                    ) : (
                                        <>
                                            <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />
                                            <button type="button" onClick={() => fileInputRef.current.click()} disabled={uploadingDoc} className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center mx-auto gap-2 transition-colors">
                                                <PaperClipIcon className="w-5 h-5"/> {uploadingDoc ? 'Subiendo...' : 'Adjuntar Archivo'}
                                            </button>
                                        </>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    {formData.documents.map((docItem, index) => (
                                        <div key={index} className="flex items-center justify-between bg-white dark:bg-gray-700 p-3 rounded-lg border dark:border-gray-600 shadow-sm transition-colors text-gray-800 dark:text-gray-200">
                                            <div className="flex items-center gap-3 overflow-hidden">
                                                <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded text-blue-600 dark:text-blue-400"><DocumentTextIcon className="w-5 h-5"/></div>
                                                <div className="truncate">
                                                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{docItem.name}</p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">{new Date(docItem.uploadedAt).toLocaleDateString()}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <a href={docItem.url} target="_blank" rel="noreferrer" className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded" title="Descargar"><ArrowDownTrayIcon className="w-5 h-5"/></a>
                                                <button type="button" onClick={() => handleDeleteDocument(index)} className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded" title="Eliminar"><TrashIcon className="w-5 h-5"/></button>
                                            </div>
                                        </div>
                                    ))}
                                    {formData.documents.length === 0 && <p className="text-center text-gray-400 italic">Sin documentos.</p>}
                                </div>
                            </div>
                        )}
                    </form>
                </div>

                {/* FOOTER */}
                <div className="p-6 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex justify-end gap-3 transition-colors">
                    <button onClick={onClose} className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">Cancelar</button>
                    <button type="submit" form="userForm" disabled={isSubmitting} className="px-6 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg font-bold hover:bg-blue-700 dark:hover:bg-blue-600 disabled:bg-blue-300 dark:disabled:bg-blue-800/50 transition-colors">
                        {isSubmitting ? 'Procesando...' : 'Guardar Cambios'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- PÁGINA PRINCIPAL ---
const UserManagementPage = ({ setPage, setNotification }) => {
    const { user } = useAuth();
    const [usersList, setUsersList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    const userPermissions = user?.permissions || {}; 
    const isSuperUser = user?.role === 'superuser';
    const isAdmin = user?.role === 'admin' || isSuperUser;

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const data = await userService.getAllUsers();
            // Filtro de invisibilidad: Superusers solo visibles por otros Superusers
            const filteredList = isSuperUser ? data : data.filter(u => u.role !== 'superuser');
            setUsersList(filteredList);
        } catch (error) {
            console.error(error);
            setNotification({ message: 'Error cargando usuarios.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) fetchUsers();
        // eslint-disable-next-line
    }, [user]);

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('¿Eliminar usuario? Esta acción es irreversible.')) return;
        try {
            await userService.deleteUser(userId);
            setNotification({ message: 'Usuario eliminado del sistema.', type: 'success' });
            fetchUsers();
        } catch (error) {
            setNotification({ message: 'Error al eliminar.', type: 'error' });
        }
    };

    if (!user) return null; 
    
    // Protección de Ruta
    if (!isAdmin && !userPermissions.hr?.view) {
        return <div className="p-8 text-center text-red-500 font-bold bg-white rounded shadow m-4">Acceso Denegado: Permisos Insuficientes.</div>;
    }

    return (
        <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors">
            {showModal && (
                <UserModal 
                    userToEdit={editingUser} 
                    onClose={() => { setShowModal(false); setEditingUser(null); }} 
                    onSave={fetchUsers}
                    setNotification={setNotification}
                    currentUser={user} 
                />
            )}

            <div className="flex items-center mb-6">
                <button onClick={() => setPage('hub')} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-semibold mr-4 flex items-center transition-colors">&larr; Volver</button>
                <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 transition-colors">Gestión de Personal</h1>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700 transition-colors">
                <div className="p-6 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex flex-col md:flex-row justify-between items-center gap-4 transition-colors">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-200 transition-colors">Directorio de Colaboradores</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors">Gestión de accesos, roles y documentación.</p>
                    </div>
                    
                    {(isAdmin || userPermissions.hr?.create) && (
                        <button onClick={() => { setEditingUser(null); setShowModal(true); }} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 flex items-center gap-2 shadow-sm transition-colors">
                            <KeyIcon className="w-5 h-5"/> Nuevo Colaborador
                        </button>
                    )}
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 transition-colors">
                        <thead className="bg-gray-50 dark:bg-gray-900 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors">
                            <tr>
                                <th className="px-6 py-3 text-left">Usuario</th>
                                <th className="px-6 py-3 text-left">Rol / Cargo</th>
                                <th className="px-6 py-3 text-center">Estado</th>
                                <th className="px-6 py-3 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 transition-colors text-gray-900 dark:text-gray-200">
                            {loading ? (
                                <tr><td colSpan="4" className="p-8 text-center text-gray-500 dark:text-gray-400">Cargando directorio...</td></tr>
                            ) : usersList.length === 0 ? (
                                <tr><td colSpan="4" className="p-8 text-center text-gray-500 dark:text-gray-400">No hay usuarios registrados.</td></tr>
                            ) : (
                                usersList.map(u => (
                                    <tr key={u.id} className="hover:bg-blue-50 dark:hover:bg-gray-700/50 transition duration-150">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center">
                                                <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center font-bold uppercase text-white ${u.role === 'admin' ? 'bg-purple-600' : u.role === 'superuser' ? 'bg-indigo-900 dark:bg-indigo-700' : 'bg-blue-500'}`}>
                                                    {(u.fullName || u.email || '?').charAt(0)}
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{u.fullName || 'Sin Nombre'}</div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">{u.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex flex-col">
                                                {u.role === 'superuser' ? (
                                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300 w-fit items-center gap-1 transition-colors">
                                                        <SparklesIcon className="w-3 h-3"/> Super Usuario
                                                    </span>
                                                ) : (
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full w-fit transition-colors ${u.role === 'admin' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'}`}>
                                                        {u.role === 'admin' ? 'Administrador' : 'Usuario'}
                                                    </span>
                                                )}
                                                <span className="text-xs text-gray-400 mt-1">{u.jobTitle || 'N/A'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            {(u.role === 'admin' || u.role === 'superuser') ? (
                                                <span className="flex items-center justify-center text-xs text-green-600 dark:text-green-400 font-medium bg-green-50 dark:bg-green-900/30 rounded-full px-2 py-1 transition-colors"><CheckCircleIcon className="w-4 h-4 mr-1"/> Total</span>
                                            ) : (
                                                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-full px-2 py-1 transition-colors">Limitado</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            {(isAdmin || userPermissions.hr?.edit) && (
                                                <button onClick={() => { setEditingUser(u); setShowModal(true); }} className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 mr-4 transition-colors" title="Editar">
                                                    <PencilSquareIcon className="w-5 h-5"/>
                                                </button>
                                            )}
                                            {u.id !== user.uid && (isAdmin || userPermissions.hr?.delete) && (
                                                <button onClick={() => handleDeleteUser(u.id)} className="text-red-400 hover:text-red-600 transition-colors" title="Eliminar">
                                                    <TrashIcon className="w-5 h-5"/>
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default UserManagementPage;